import { useEffect, useRef, useState } from 'react';
import { ArrowSquareOut, CaretDown } from 'shared/icons';
import { ML_SHORTCUT_GROUPS } from '../../utils/ml';

/**
 * Botão "ML" + dropdown com os atalhos do Seller Center.
 *
 * Junta num lugar só os hyperlinks que estavam espalhados por sete páginas do
 * Campaign Manager legado (v1 e v2) — a lista e o agrupamento vivem em
 * `utils/ml.js` (`ML_SHORTCUT_GROUPS`), não aqui: URL de ML nunca fica solta
 * dentro de componente (CLAUDE.md §4).
 *
 * Fecha no clique fora e no Escape, como o resto dos popovers do app
 * (`pages/operacoes/components/MlbListingsPopover`).
 */
const MlShortcutsMenu = () => {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const handleClickOutside = (event) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        const handleKey = (event) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKey);
        };
    }, [open]);

    return (
        <div ref={wrapRef} className="relative">
            <button
                type="button"
                onClick={() => setOpen((value) => !value)}
                aria-expanded={open}
                aria-haspopup="menu"
                title="Atalhos do Mercado Livre"
                className="flex items-center gap-1 rounded-lg border border-mono-300 bg-white px-2.5 py-1.5 text-xs font-medium text-mono-700 transition-colors hover:border-mono-400 hover:text-black dark:border-mono-700 dark:bg-mono-900 dark:text-mono-200 dark:hover:border-mono-500 dark:hover:text-white"
            >
                ML
                <CaretDown size={12} aria-hidden="true" className={open ? 'rotate-180 transition-transform' : ''} />
            </button>

            {open ? (
                // `right-0`: o menu abre alinhado à direita porque o botão fica no
                // canto direito da barra — abrindo à esquerda ele sairia da tela.
                <div
                    role="menu"
                    className="absolute right-0 top-[calc(100%+4px)] z-50 max-h-[70vh] w-60 overflow-y-auto rounded-xl border border-mono-200 bg-white p-1.5 shadow-xl dark:border-mono-800 dark:bg-mono-950"
                >
                    {ML_SHORTCUT_GROUPS.map((group) => (
                        <div key={group.id} className="mb-1 last:mb-0">
                            <p className="px-2 py-1 text-[9px] font-bold uppercase tracking-wide text-mono-400">
                                {group.label}
                            </p>
                            {group.links.map((link) => (
                                <a
                                    key={link.id}
                                    role="menuitem"
                                    href={link.href}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() => setOpen(false)}
                                    className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-xs text-mono-700 transition-colors hover:bg-mono-100 hover:text-black dark:text-mono-300 dark:hover:bg-mono-900 dark:hover:text-white"
                                >
                                    <span className="truncate">{link.label}</span>
                                    <ArrowSquareOut size={12} aria-hidden="true" className="shrink-0 text-mono-400" />
                                </a>
                            ))}
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

export default MlShortcutsMenu;
